import { ab } from './interface'
import {
  Experiment,
  AudioHighlight982Variant,
  TrialByPlanV2_18369Variant,
  IosExternalLinkPayment16218Variant,
  NullExperiment17503Variant,
  FirstGuideActivation17817Variant,
  TimeToRead17958Variant,
  FreeUserUpgrade18151Variant,
  PasswordlessSignup18304Variant,
  OnboardingV9EntryPersonalization18361Variant,
  HomepageHeroSearch18446Variant,
  CancelSaveFlow17977Variant,
  FreeBookOfTheDay18484Variant,
  PdfPageScrollExitModal18683Variant,
} from './types'

import { runTextUpdates } from '@/services/ab/13448_ab_experiment'
import { detectAndSaveEntryBook18361 } from '@/services/ab/18361_entry_personalization'
import { auth } from '@/services/auth'
import Cookies from 'js-cookie'
import { platform } from '@/services/platform'
import { platformData } from '@/services/platform.capacitor'
import { getIosStorefrontLocation } from '@/services/capacitor.storefrontLocationPlugin'
import { pushABExperimentsToDataLayer } from '@/analytics/gtm'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'

/**
 * Initialize CloudFront experiments.
 *
 * Check A/B testing cookies set by CloudFront, send backend
 * requests and update frontend a/b data storage.
 * Pushes AB experiments to GTM data layer when done.
 */
export async function initAB(): Promise<void> {
  await newCopy13448Experiment(Experiment.new_copy_pdf_v2_13448, '/pdf')
  await aiGuideFlow13747()
  await aiOldVsPbp18613()
  await homepageHeroSearch18446()
  await summariesPageRedesign18591()
  pushABExperimentsToDataLayer()
}

/**
 * Initializes Public Summary pages specific CloudFront experiments.
 * Calls {@link initAB}
 */
export async function initSummaryAB(): Promise<void> {
  // Add public summary specific experiment here

  await initAB()
}

/**
 * Initializes PDF pages specific CloudFront experiments.
 * Calls {@link initAB}
 */
export async function initPdfAB(): Promise<void> {
  await pdfPageScrollExitModal18683Experiment()
  await initAB()
}

/*
 * Homepage hero search experiment.
 *
 * A - Control: current homepage.
 * B - Variant: restored 17886 B hero with the search bar.
 */
export async function homepageHeroSearch18446Experiment(): Promise<HomepageHeroSearch18446Variant> {
  let result = ab.experimentVariant(Experiment.exp_homepage_hero_search_18446)

  if (result === null) {
    result = await ab.start(Experiment.exp_homepage_hero_search_18446)
  }

  return result as HomepageHeroSearch18446Variant
}

export function homepageHeroSearch18446Variant(): HomepageHeroSearch18446Variant {
  return ab.experimentVariant(
    Experiment.exp_homepage_hero_search_18446,
  ) as HomepageHeroSearch18446Variant
}

/*
 * Read the homepage search experiment cookie set by CloudFront and transfer
 * the assignment to local A/B storage.
 */
export async function homepageHeroSearch18446(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-Homepage-Hero-Search-Bar-18446')
  const localVariant = ab.experimentVariant(
    Experiment.exp_homepage_hero_search_18446,
  )

  if (cfVariant && !localVariant) {
    await ab.start(Experiment.exp_homepage_hero_search_18446, cfVariant)
  }
}

/**
 * Feature flag to enable/disable Google Billing. Disabled by default.
 *
 * This is not a real A/B experiment, i.e. the experiment is not activated on
 * the backend.
 */
export function googleBillingEnabled(): boolean {
  return ab.experimentVariant(Experiment.google_billing_7517) === 'yes'
}

/**
 * Apply the text changes on the public pages
 *
 * @param {Experiment} experiment - The A/B experiment containing the text changes.
 * @param {string} allowedUrlExpression - A regular expression to match allowed URLs,
 * on which the text changes should be applied.
 */
export async function newCopy13448Experiment(
  experiment: Experiment,
  allowedUrlExpression: string,
): Promise<void> {
  const pathname = window.location.pathname
  const isTranslatedPage = pathname.includes(`/es${allowedUrlExpression}/`)

  if (!pathname.includes(allowedUrlExpression) || isTranslatedPage) {
    // Only start experiment on non-translated pdf pages
    return
  }

  let result = ab.experimentVariant(experiment)

  if (result === null) {
    result = await ab.start(experiment)
  }

  if (result === 'yes') {
    runTextUpdates(experiment)
  }
}

/**
 * Highlight the text on summary pages along with the audio being played.
 */
export function audioHighlight982ExperimentVariant(): AudioHighlight982Variant {
  return ab.experimentVariant(
    Experiment.exp_audio_highlight_982,
  ) as AudioHighlight982Variant
}

/**
 * Highlight the text on summary pages along with the audio being played.
 */
export async function audioHighlight982Experiment(): Promise<AudioHighlight982Variant> {
  let result = ab.experimentVariant(Experiment.exp_audio_highlight_982)

  if (result === null) {
    result = await ab.start(Experiment.exp_audio_highlight_982)
  }

  return result as AudioHighlight982Variant
}

/**
 * Get the variant of the ai_label_13537 experiment.
 * Returns boolean:
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export function aiLabel13537ExperimentVariant(): boolean {
  return ab.experimentVariant(Experiment.ai_label_13537) === 'yes'
}

/**
 * Decide wheter or not to render the ai content label component in
 * the book preview and summary pages.
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export async function aiLabel13537Experiment(): Promise<boolean> {
  let result = ab.experimentVariant(Experiment.ai_label_13537)

  if (result === null) {
    result = await ab.start(Experiment.ai_label_13537)
  }

  return result === 'yes'
}

/*
 * Read the ai_guide_flow_13747 experiment cookies set by CloudFront and
 * transfer the experiment assignment to the local storage
 */
export async function aiGuideFlow13747(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-AI-Guide-Flow-13747')
  const localVariant = ab.experimentVariant(Experiment.ai_guide_flow_13747)

  if (cfVariant && !localVariant) {
    await ab.start(Experiment.ai_guide_flow_13747, cfVariant)
  }
}

/*
 * Read the ai_old_vs_pbp_18613 experiment cookies set by CloudFront and
 * transfer the experiment assignment to the local storage
 */
export async function aiOldVsPbp18613(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-AI-Old-Vs-Pbp-18613')
  const localVariant = ab.experimentVariant(Experiment.ai_old_vs_pbp_18613)

  if (cfVariant && !localVariant) {
    await ab.start(Experiment.ai_old_vs_pbp_18613, cfVariant)
  }
}

/**
 * Trial by Plan V2 experiment.
 *
 * A - Control: current experience without the trial by plan feature.
 * B - Variant: monthly and annual plans have different trial lengths
 *     (no trial for monthly, 14 days for annual).
 */
export async function trialByPlanV2_18369Experiment(): Promise<TrialByPlanV2_18369Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_trial_by_plan_v2_18369,
  ) as TrialByPlanV2_18369Variant

  const isAndroid = await platform.isAndroid()
  const isNativeIOSApp = await platform.isNativeIOSApp()

  if (isAndroid || isNativeIOSApp) {
    return null
  }

  if (result === null) {
    result = (await ab.start(
      Experiment.exp_trial_by_plan_v2_18369,
    )) as TrialByPlanV2_18369Variant
  }

  return result
}

export function trialByPlanV2_18369Variant(): TrialByPlanV2_18369Variant {
  return ab.experimentVariant(
    Experiment.exp_trial_by_plan_v2_18369,
  ) as TrialByPlanV2_18369Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export async function iosExternalLinkPayment16218Experiment(): Promise<IosExternalLinkPayment16218Variant> {
  await platformData.init()
  const appBuild = platformData.appBuild
  // We need to get the storefront location for the experiment
  // and it's not available in builds < 60.
  if (appBuild < 60) {
    return null
  }
  const storefrontLocation = await getIosStorefrontLocation()
  // External payments are allowed in the US storefront only.
  if (storefrontLocation !== 'USA') {
    return null
  }

  // Check variant in the local storage
  let result = ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  )

  // Start the experiment
  if (result === null) {
    result = await ab.start(Experiment.exp_ios_external_link_payment_16218)
  }

  return result as IosExternalLinkPayment16218Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export function iosExternalLinkPayment16218Variant(): IosExternalLinkPayment16218Variant {
  return ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  ) as IosExternalLinkPayment16218Variant
}

/**
 * Start the exp_null_experiment_17503 experiment on signup page visit.
 *
 * A - Control, no changes.
 * B - Variant, no changes.
 */
export async function signupPageNullExperiment17503Experiment(): Promise<NullExperiment17503Variant> {
  let result = ab.experimentVariant(Experiment.exp_null_experiment_17503)

  if (result === null) {
    result = await ab.start(Experiment.exp_null_experiment_17503)
  }

  return result as NullExperiment17503Variant
}

/**
 * Start the exp_first_guide_activation_17817 experiment at the end of
 * onboarding.
 *
 * A - Control: existing flow after onboarding.
 * B - Variant: display "first guide" popup after onboarding.
 */
export async function firstGuideActivation17817Experiment(): Promise<FirstGuideActivation17817Variant> {
  let result = ab.experimentVariant(Experiment.exp_first_guide_activation_17817)

  if (result === null) {
    result = await ab.start(Experiment.exp_first_guide_activation_17817)
  }

  return result as FirstGuideActivation17817Variant
}

/**
 * Get the variant for the exp_first_guide_activation_17817 experiment.
 */
export function firstGuideActivation17817Variant(): FirstGuideActivation17817Variant {
  return ab.experimentVariant(
    Experiment.exp_first_guide_activation_17817,
  ) as FirstGuideActivation17817Variant
}

/**
 * Start the exp_time_to_read_17958 experiment from the Layout component.
 *
 * A - Control: no time estimates.
 * B - Variant: display time estimates on doc cards.
 */
export async function timeToRead17958Experiment(): Promise<TimeToRead17958Variant> {
  let result = ab.experimentVariant(Experiment.exp_time_to_read_17958)

  if (result === null) {
    result = await ab.start(Experiment.exp_time_to_read_17958)
  }

  return result as TimeToRead17958Variant
}

/**
 * Get the variant for the exp_time_to_read_17958 experiment.
 */
export function timeToRead17958Variant(): TimeToRead17958Variant {
  return ab.experimentVariant(
    Experiment.exp_time_to_read_17958,
  ) as TimeToRead17958Variant
}

/**
 * Start the exp_free_user_upgrade_18151 when navigating from the billing page.
 *
 * We exclude these users from the experiment;
 * - users created after the experiment started,
 * - users that already started the free trial,
 * - paid users.
 *
 * A - Control: Current experience. No redirect, no modal, CTAs unchanged.
 * B - Variant: With the following changes:
 * - return-to-billing redirect on every return visit until trial start,
 * - one-time modal triggered on first page navigation off the billing page,
 * - nav CTA copy updated from "Free Trial" to "Start Free Trial",
 * - gated content CTA copy updated from "Subscribe to Read Full Summary" to "Start Free Trial to Unlock"
 *
 * The experiment is paused: we no longer start new assignments (no backend
 * request). Users keep whatever variant is already in local storage, and
 * everyone else falls back to the control variant `A`.
 */
export async function freeUserUpgrade18151Experiment(): Promise<FreeUserUpgrade18151Variant> {
  const result =
    ab.experimentVariant(Experiment.exp_free_user_upgrade_18151) ?? 'A'

  if (result === 'B') {
    storage.setItem(StorageKeys.upgradePromptModal18151, 'true')
  }

  return result as FreeUserUpgrade18151Variant
}

/**
 * Get the variant for the exp_free_user_upgrade_18151 experiment.
 */
export function freeUserUpgrade18151Variant(): FreeUserUpgrade18151Variant {
  const result = ab.experimentVariant(Experiment.exp_free_user_upgrade_18151)

  return result as FreeUserUpgrade18151Variant
}

/**
 * Start the exp_passwordless_signup_18304 experiment.
 *
 * A - Control: existing signup flow with password.
 * B - Variant: passwordless signup flow.
 */
export async function passwordlessSignup18304Experiment(): Promise<PasswordlessSignup18304Variant> {
  return passwordlessSignup18304Variant()
}

/**
 * Get the variant for the exp_passwordless_signup_18304 experiment.
 */
export function passwordlessSignup18304Variant(): PasswordlessSignup18304Variant {
  const result = ab.experimentVariant(
    Experiment.exp_passwordless_signup_18304,
  ) as PasswordlessSignup18304Variant

  if (!result && window.__UNIT_TESTING) {
    return 'A'
  }

  return result ?? 'B'
}

/**
 * Start the exp_cancel_save_flow_17977 experiment when the user clicks the
 * cancel-subscription link.
 *
 * A - Control: existing cancel popup, no save flow.
 * B - Variant: show the save flow on "End my personal growth".
 */
export async function cancelSaveFlow17977Experiment(): Promise<CancelSaveFlow17977Variant> {
  let result = ab.experimentVariant(Experiment.exp_cancel_save_flow_17977)

  if (result === null) {
    result = await ab.start(Experiment.exp_cancel_save_flow_17977)
  }

  return result as CancelSaveFlow17977Variant
}

/**
 * Get the assigned variant for the cancel_save_flow_17977 experiment.
 */
export function cancelSaveFlow17977Variant(): CancelSaveFlow17977Variant {
  return ab.experimentVariant(
    Experiment.exp_cancel_save_flow_17977,
  ) as CancelSaveFlow17977Variant
}

/**
 * Start the exp_free_botd_18484 experiment at the end of
 * onboarding.
 *
 * A - Control: no free book display or notifications.
 * B - Variant: display "free book of the day" on discover page; send
 *     email and push notifications.
 */
export async function freeBookOfTheDay18484Experiment(): Promise<FreeBookOfTheDay18484Variant> {
  let result = ab.experimentVariant(Experiment.exp_free_botd_18484)

  if (result === null) {
    result = await ab.start(Experiment.exp_free_botd_18484)
  }

  return result as FreeBookOfTheDay18484Variant
}

/**
 * Get the variant for the exp_free_botd_18484 experiment.
 */
export function freeBookOfTheDay18484Variant(): FreeBookOfTheDay18484Variant {
  return ab.experimentVariant(
    Experiment.exp_free_botd_18484,
  ) as FreeBookOfTheDay18484Variant
}

/**
 * Shortcut to check whether to display free_botd_18484 features.
 *
 * null, A: no
 * B and has ever started a free trial: no
 * B and is currently subscribed: no
 * B and neither of the above: yes
 */
export function showFreeBookOfTheDay18484(): boolean {
  const variant = freeBookOfTheDay18484Variant()
  return variant === 'B' && auth.isFreePreTrialUser18484()
}

/**
 * Start the exp_onboarding_v9_entry_personalization_18361 experiment after signup
 * when the user first visited a valid summary or PDF page.
 *
 * A - Control: existing onboarding.
 * B - Variant: onboarding personalized to the entry book.
 */
export async function onboardingEntryPersonalization18361Experiment(): Promise<OnboardingV9EntryPersonalization18361Variant> {
  const entryBook = await detectAndSaveEntryBook18361()
  if (!entryBook) {
    return null
  }

  let result = ab.experimentVariant(
    Experiment.exp_onboarding_v9_entry_personalization_18361,
  )

  if (result === null) {
    result = await ab.start(
      Experiment.exp_onboarding_v9_entry_personalization_18361,
    )
  }

  return result as OnboardingV9EntryPersonalization18361Variant
}

/**
 * Get the variant for the exp_onboarding_v9_entry_personalization_18361 experiment.
 */
export function onboardingEntryPersonalization18361Variant(): OnboardingV9EntryPersonalization18361Variant {
  return ab.experimentVariant(
    Experiment.exp_onboarding_v9_entry_personalization_18361,
  ) as OnboardingV9EntryPersonalization18361Variant
}

/**
 * Read the summaries page redesign experiment cookies set by CloudFront
 * and transfer the experiment assignment to the local storage.
 */
export async function summariesPageRedesign18591(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-Summaries-Page-Redesign-18591')
  const localVariant = ab.experimentVariant(
    Experiment.exp_summaries_page_redesign_18591,
  )

  if (cfVariant && !localVariant) {
    await ab.start(Experiment.exp_summaries_page_redesign_18591, cfVariant)
  }
}

/**
 * Start the exp_pdf_page_scroll_exit_modal_relaunch_18683 experiment in pdf pages.
 *
 * A - Control: Existing embedded CTAs only. No modal of any kind.
 * B - Variant: signup modal triggered at 30% scroll depth, exit intent fires the modal once more.
 * If user clicks on any of the signup cta's the modal fires once more.
 */
export async function pdfPageScrollExitModal18683Experiment(): Promise<PdfPageScrollExitModal18683Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_pdf_page_scroll_exit_modal_relaunch_18683,
  )

  if (result === null) {
    result = await ab.start(
      Experiment.exp_pdf_page_scroll_exit_modal_relaunch_18683,
    )
  }

  return result as PdfPageScrollExitModal18683Variant
}

/**
 * Get the variant for the exp_pdf_page_scroll_exit_modal_relaunch_18683 experiment.
 */
export function pdfPageScrollExitModal18683Variant(): PdfPageScrollExitModal18683Variant {
  const result = ab.experimentVariant(
    Experiment.exp_pdf_page_scroll_exit_modal_relaunch_18683,
  )

  return result as PdfPageScrollExitModal18683Variant
}
